import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import {
  Lightbulb, CheckCircle, ArrowRight, Loader2, Layers, Puzzle, Code2, Server, MessagesSquare, Rocket
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const EXT_TYPES = [
  { value: 'Component', icon: Layers },
  { value: 'Module', icon: Puzzle },
  { value: 'Plugin', icon: Code2 },
  { value: 'Template', icon: Server },
  { value: 'Not sure yet', icon: MessagesSquare },
];

const SuggestExtensionPage = () => {
  const [formData, setFormData] = useState({
    name: '', email: '',
    ideaTitle: '', extensionType: '', joomlaVersions: ['4', '5', '6'],
    problem: '', description: '', wouldPay: '', agreedToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const toggleJoomlaVersion = (v) => {
    setFormData((prev) => ({
      ...prev,
      joomlaVersions: prev.joomlaVersions.includes(v)
        ? prev.joomlaVersions.filter((x) => x !== v)
        : [...prev.joomlaVersions, v].sort(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const joomlaStr = formData.joomlaVersions.map((v) => `Joomla ${v}`).join(', ') || 'Any';
    const message = [
      `=== EXTENSION SUGGESTION ===`,
      `Suggested by: ${formData.name || 'Anonymous'}`,
      `Email: ${formData.email || 'Not provided'}`,
      ``,
      `Idea: ${formData.ideaTitle}`,
      `Type: ${formData.extensionType || 'Not sure'}`,
      `Target Joomla Versions: ${joomlaStr}`,
      `Would pay for it: ${formData.wouldPay || 'Not specified'}`,
      ``,
      `Problem it solves:`,
      formData.problem || 'N/A',
      ``,
      `Details:`,
      formData.description || 'N/A',
    ].join('\n');

    const tags = [
      'Extension Suggestion',
      formData.extensionType,
      `Joomla ${formData.joomlaVersions.join('/')}`,
      formData.wouldPay === 'Yes' ? 'Willing to Pay' : null,
    ].filter(Boolean);

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name || 'Anonymous Suggestion',
          email: formData.email || 'no-reply@aijoomla.org',
          subject: `Extension Suggestion: ${formData.ideaTitle}`,
          message,
          source: 'extension_suggestion',
          opportunity_name: `Suggestion – ${formData.ideaTitle}`,
          opportunity_source: 'Extension Suggestion Page',
          stage: 'New Lead',
          tags,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success('Thanks! Your suggestion has been submitted.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 focus:outline-none transition-colors';
  const labelCls = 'block text-sm font-medium text-gray-300 mb-2';

  return (
    <div className="min-h-screen bg-bots-dark" data-testid="suggest-extension-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-14">
        <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-purple-600/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-sm font-semibold mb-6">
            <Lightbulb className="w-4 h-4" /> Shape our Roadmap
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5">
            Suggest an{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">extension</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Got an idea for a Joomla component, module, plugin or template you wish existed?
            Tell us — popular suggestions get prioritized and built for Joomla 4, 5 &amp; 6.
          </p>
          <Link to="/roadmap" className="inline-flex items-center gap-2 mt-6 text-blue-400 hover:text-blue-300 font-semibold" data-testid="view-roadmap-link">
            <Rocket className="w-4 h-4" /> See what's already on the roadmap
          </Link>
        </div>
      </section>

      {/* Form */}
      <section className="pb-20">
        <div className="max-w-2xl mx-auto px-4">
          {submitted ? (
            <div className="text-center bg-bots-surface border border-blue-500/20 rounded-2xl p-12" data-testid="suggest-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-white mb-3">Suggestion received!</h3>
              <p className="text-gray-300 max-w-md mx-auto mb-6">
                Thank you for helping shape our roadmap. If it gains traction, you'll see it appear on our public roadmap.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/roadmap" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-all">
                  View the roadmap <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', ideaTitle: '', extensionType: '', joomlaVersions: ['4', '5', '6'], problem: '', description: '', wouldPay: '', agreedToTerms: false }); }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all"
                >
                  Suggest another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-bots-surface border border-blue-500/20 rounded-2xl p-6 md:p-8 space-y-6" data-testid="suggest-extension-form">
              <div>
                <label className={labelCls}>Your extension idea *</label>
                <input type="text" required placeholder="e.g. AI-powered spam filter for Joomla forms"
                  value={formData.ideaTitle}
                  onChange={(e) => setFormData({ ...formData, ideaTitle: e.target.value })}
                  className={inputCls} data-testid="sx-idea" />
              </div>

              {/* Type */}
              <div>
                <label className={labelCls}>What kind of extension?</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {EXT_TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = formData.extensionType === t.value;
                    return (
                      <button key={t.value} type="button"
                        onClick={() => setFormData({ ...formData, extensionType: t.value })}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border text-xs font-medium transition-all ${
                          active ? 'border-blue-400 bg-blue-500/10 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                        }`}
                        data-testid={`sx-type-${t.value.replace(/\s+/g, '-').toLowerCase()}`}>
                        <Icon className={`w-5 h-5 ${active ? 'text-blue-400' : ''}`} />
                        {t.value}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Joomla versions */}
              <div>
                <label className={labelCls}>Target Joomla versions</label>
                <div className="flex flex-wrap gap-3" data-testid="sx-joomla-versions">
                  {['4', '5', '6'].map((v) => {
                    const active = formData.joomlaVersions.includes(v);
                    return (
                      <button key={v} type="button" onClick={() => toggleJoomlaVersion(v)}
                        className={`px-5 py-2.5 rounded-lg border font-semibold transition-all ${
                          active ? 'border-blue-400 bg-blue-500/10 text-blue-300' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                        }`}
                        data-testid={`sx-joomla-${v}`}>
                        {active && <CheckCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />}Joomla {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={labelCls}>What problem would it solve? *</label>
                <textarea required rows={3} placeholder="Describe the pain point this extension would fix..."
                  value={formData.problem}
                  onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                  className={`${inputCls} resize-none`} data-testid="sx-problem" />
              </div>

              <div>
                <label className={labelCls}>Any extra details (optional)</label>
                <textarea rows={3} placeholder="Features, integrations, examples you like..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`${inputCls} resize-none`} data-testid="sx-description" />
              </div>

              <div>
                <label className={labelCls}>Would you pay for this extension?</label>
                <select value={formData.wouldPay}
                  onChange={(e) => setFormData({ ...formData, wouldPay: e.target.value })} className={inputCls} data-testid="sx-would-pay">
                  <option value="">Select an answer</option>
                  <option value="Yes">Yes, I'd buy it</option>
                  <option value="Maybe">Maybe, depends on price</option>
                  <option value="Free only">Only if it's free</option>
                </select>
              </div>

              <div className="border-t border-white/10 pt-6 grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Your name (optional)</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} data-testid="sx-name" />
                </div>
                <div>
                  <label className={labelCls}>Email (optional — for updates)</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} data-testid="sx-email" />
                </div>
              </div>

              <button type="submit" disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold rounded-full hover:from-purple-500 hover:to-indigo-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="sx-submit">
                {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>) : (<><Lightbulb className="w-5 h-5" /> Submit Suggestion</>)}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SuggestExtensionPage;
